import { useEffect } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import RadioButton from 'components/form/RadioButton'
import CategoryFilter from 'features/editlog/filters/CategoryFilter'
import moment from 'moment'
import {
  clearSelectedGroup,
  clearSelectedKeyword,
  fetchCampaigns,
  fetchChannels,
  fetchEditlogs,
  fetchGroups,
  fetchKeywords,
  inputActor,
  inputQuery,
  refresh,
  SEARCH_LAYOUT,
  SELECT_LAYOUT,
  selectAggregateType,
  setPage,
  setRange,
  setSearchFormLayout,
} from 'features/editlog/editlogSlice'
import CampaignFilter from 'features/editlog/filters/CampaignFilter'
import GroupFilter from 'features/editlog/filters/GroupFilter'
import KeywordFilter from 'features/editlog/filters/KeywordFilter'
import AggregateTypeFilter from 'features/editlog/filters/AggregateTypeFilter'
import ChannelFilter from 'features/editlog/filters/ChannelFilter'
import DateRangePicker from 'components/calendar/DateRangePicker'
import useCalendarRange from 'components/calendar/useCalendarRange'
import TextInput from 'components/form/TextInput'
import { CALENDAR_PRESETS, maximum } from 'components/calendar/calendarUtil'
import { fail } from 'features/toast/Toast'
import NoneAnchor from 'components/NoneAnchor'
import { diffDays } from '../dashboard/info/chart/chartUtil'

const FIRST_PAGE = 0

const EditlogSearchForm = () => {
  const dispatch = useDispatch()
  const { selectedCampaign, selectedGroup, searchFormLayout, page } =
    useSelector(state => state.editlog)
  const { selectedAggregateType } = useSelector(state => state.editlog)
  const { startDt, endDt, preset, actor, aggregateName } = useSelector(
    state => state.editlog.formValues
  )
  const { min, max } = useCalendarRange(moment().subtract(2, 'year'))

  useEffect(() => {
    dispatch(fetchChannels())
  }, [dispatch])

  useEffect(() => {
    if (selectedCampaign) {
      dispatch(fetchGroups(selectedCampaign.id))
    }
    return () => dispatch(clearSelectedGroup())
  }, [dispatch, selectedCampaign])

  useEffect(() => {
    if (selectedGroup) {
      dispatch(fetchKeywords(selectedGroup.id))
    }
    return () => dispatch(clearSelectedKeyword())
  }, [dispatch, selectedGroup])

  const formReset = () => {
    batch(() => {
      dispatch(refresh())
      dispatch(fetchChannels())
      dispatch(fetchCampaigns())
      dispatch(fetchEditlogs())
    })
  }

  const handleRangeChange = (start, end, presetValue) => {
    if (diffDays(start, end) >= 92) {
      fail('기간은 최대 92일까지 설정할 수 있습니다.\n날짜를 재설정해주세요.')
      return false
    } else {
      batch(() => {
        dispatch(
          setRange({
            start,
            end,
            presetValue,
          })
        )
        if (page > 0) {
          dispatch(setPage(0))
        }
      })
      dispatch(fetchEditlogs())
    }
  }

  const isValid = () => {
    if (searchFormLayout === SEARCH_LAYOUT) {
      if (actor) return true
      if (!selectedAggregateType) {
        fail('유형을 선택해 주세요.')
        return false
      }
    }
    return true
  }

  const onSubmit = () => {
    if (isValid()) {
      dispatch(setPage(FIRST_PAGE))
      dispatch(fetchEditlogs())
    }
  }

  return (
    <>
      <div className="managebox_tit">
        <div className="f_left">
          <div className="single_wrap">
            <RadioButton.Group className="filter_radio" focusOnError>
              <RadioButton
                name="layout"
                value={SELECT_LAYOUT}
                label="변경이력 선택 검색"
                checked={searchFormLayout === SELECT_LAYOUT}
                onClick={() => dispatch(setSearchFormLayout(SELECT_LAYOUT))}
              />
              <RadioButton
                name="layout"
                value={SEARCH_LAYOUT}
                label="변경이력 직접 검색"
                checked={searchFormLayout === SEARCH_LAYOUT}
                onClick={() => dispatch(setSearchFormLayout(SEARCH_LAYOUT))}
              />
            </RadioButton.Group>
          </div>
        </div>
        <div className="f_right">
          <CategoryFilter />
          <div className="single_wrap">
            <div className="group_calendar">
              <DateRangePicker
                start={maximum(min, startDt)}
                end={endDt}
                preset={preset}
                min={min}
                max={max}
                onChange={handleRangeChange}
                presetExcludes={[CALENDAR_PRESETS.ALL]}
                presetTooltip={{
                  preset: CALENDAR_PRESETS.CUSTOM,
                  text: '맞춤 설정 기간은 최대 92일 이내로 설정 가능합니다.',
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="managebox_tit">
        <div className="f_left">
          {searchFormLayout === SELECT_LAYOUT && (
            <>
              <div className="single_wrap">
                <ChannelFilter />
              </div>
              <div className="single_wrap">
                <CampaignFilter />
              </div>
              <div className="single_wrap">
                <GroupFilter />
              </div>
              <div className="single_wrap">
                <KeywordFilter />
              </div>
            </>
          )}
          {searchFormLayout === SEARCH_LAYOUT && (
            <>
              <div className="search_box">
                <AggregateTypeFilter
                  onSelectAggregateType={v => dispatch(selectAggregateType(v))}
                />
                <TextInput
                  placeholder="이름을 입력하세요"
                  value={aggregateName}
                  className="box_inptxt search_inp2"
                  onValueChange={v => dispatch(inputQuery(v))}
                />
              </div>
            </>
          )}
          <div className="single_wrap">
            <TextInput
              placeholder="처리자 검색"
              value={actor}
              className="box_inptxt search_inp2"
              onValueChange={v => dispatch(inputActor(v))}
            />
          </div>
        </div>
        <div className="f_right">
          <div className="single_wrap">
            <NoneAnchor className="btn_gm gm_bl" onClick={onSubmit}>
              <span className="inner_g">선택 값 적용</span>
            </NoneAnchor>
          </div>
          <div className="single_wrap">
            <NoneAnchor className="btn_gm gm_line" onClick={formReset}>
              <span className="inner_g">새로고침</span>
            </NoneAnchor>
          </div>
        </div>
      </div>
    </>
  )
}

export default EditlogSearchForm
